import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Building2, Home, Cable } from 'lucide-react';
import { SHOP_ICPS } from '@evi/store-kit';
import { FilmHero } from '@/components/FilmHero';
import styles from './LandingPage.module.css';

// DC-first hero film: the branded DC product film (a montage of the pedestal,
// close-ups and the app), transcoded to the shop's UGC film convention
// (portrait 9:16). FilmHero shows about half of it on a wide screen;
// HERO_FRAMING picks which half (40%: the montage's subjects sit mid-frame).
const HERO_VIDEO = '/media/ugc/landing.mp4';
const HERO_POSTER = '/media/ugc/landing-poster.jpg';
const HERO_FRAMING = 40;

/** Category tiles, in the locked menu order (DC leads). */
const CATEGORIES = [
  {
    to: '/commercial-dc',
    label: 'Commercial DC',
    blurb: 'DC fast charging, 40kW to 240kW. Pre-commissioning to the network included.',
    icon: Zap,
    featured: true,
  },
  {
    to: '/commercial-ac',
    label: 'Commercial AC',
    blurb: 'Level 2 for workplaces, fleets, and multifamily. Up to 19.2 kW.',
    icon: Building2,
    featured: false,
  },
  {
    to: '/home-charging',
    label: 'Home Charging',
    blurb: 'Level 2 home chargers. The AC Elite Commercial joins the EV Initiative network.',
    icon: Home,
    featured: false,
  },
  {
    to: '/accessories',
    label: 'Accessories',
    blurb: 'Pedestals, RFID cards, and mounting hardware.',
    icon: Cable,
    featured: false,
  },
] as const;

export function LandingPage() {
  return (
    <div>
      {/* DC-first hero (components/FilmHero, as on WhyPage and
          CommercialDcPage): full width, outside the page's max-width column. */}
      <FilmHero video={HERO_VIDEO} poster={HERO_POSTER} framing={HERO_FRAMING} label="Commercial DC fast charging">
        <p className={styles.eyebrow}>Commercial DC fast charging</p>
        <h1 className={styles.heroTitle}>
          DC fast charging,
          <br />
          delivered and commissioned.
        </h1>
        <p className={styles.heroSub}>
          Autel MaxiCharger DC and DC Compact, priced as one inclusive number that
          <strong> includes pre-commissioning to the network</strong>. Buy online and ship
          to site. Each charger goes live after it connects, passes its plug test, and
          EV Initiative approves the launch.
        </p>
        <div className={styles.heroActions}>
          <Link to="/commercial-dc" className="eviBtn eviBtnPrimary">
            Shop Commercial DC <ArrowRight size={18} />
          </Link>
          <Link to="/commercial-ac" className="eviBtn eviBtnGhost">
            Commercial AC
          </Link>
        </div>
      </FilmHero>

      <div className={styles.page}>
        <section className={styles.categories} aria-label="Shop by category">
          {CATEGORIES.map(({ to, label, blurb, icon: Icon, featured }) => (
            <Link
              key={to}
              to={to}
              className={featured ? `${styles.card} ${styles.cardFeatured}` : styles.card}
            >
              <span className={styles.cardIcon}>
                <Icon size={22} />
              </span>
              <span className={styles.cardBody}>
                <span className={styles.cardLabel}>{label}</span>
                <span className={styles.cardBlurb}>{blurb}</span>
              </span>
              <ArrowRight size={18} className={styles.cardArrow} />
            </Link>
          ))}
        </section>

        <section className={styles.who} aria-label="Who it's for">
          <h2 className={styles.whoTitle}>Who it&rsquo;s for</h2>
          <div className={styles.whoGrid}>
            {SHOP_ICPS.map((icp) => (
              <Link key={icp.slug} to={`/why/${icp.slug}`} className={styles.whoCard}>
                <div className={styles.whoMedia}>
                  <img
                    src={icp.tile}
                    alt=""
                    loading="lazy"
                    style={{ objectPosition: `center ${icp.tileFraming ?? 50}%` }}
                  />
                </div>
                <div className={styles.whoBody}>
                  <span className={styles.whoEyebrow}>{icp.eyebrow}</span>
                  <span className={styles.whoLine}>{icp.title}</span>
                  <span className={styles.whoCta}>
                    Learn more <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
